import { IconButton, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { TextField } from '../../common/text-field';

interface LogsHeaderProps {
  search: string;
  onSearchChange: (value: string) => void;
}

const DEBOUNCE_MS = 500;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 0 24px',
    height: 64,
    boxSizing: 'border-box',
  },
  searchInput: {
    flex: 1,
  },
});

const LogsHeader = memo(function LogsHeader({
  search,
  onSearchChange,
}: LogsHeaderProps) {
  const classes = useStyles();
  const [local, setLocal] = useState(search);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocal(search);
  }, [search]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setLocal(v);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onSearchChange(v), DEBOUNCE_MS);
    },
    [onSearchChange],
  );

  const handleClear = useCallback(() => {
    setLocal('');
    if (timerRef.current) clearTimeout(timerRef.current);
    onSearchChange('');
  }, [onSearchChange]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className={classes.root} data-testid='logs-header'>
      <TextField
        placeholder='Search logs...'
        value={local}
        onChange={handleChange}
        wrapperClassName={classes.searchInput}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: local ? (
            <InputAdornment position='end'>
              <IconButton
                size='small'
                onClick={handleClear}
                aria-label='Clear search'
              >
                <CloseIcon fontSize='small' />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        small
        data-testid='logs-search'
      />
    </div>
  );
});

export default LogsHeader;
